import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Accordion from '@repo/ui/accordion'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { ClockIcon, PlusIcon, PrintIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useEffect, useMemo, useRef, useState } from 'react'

import { API, constructApiUrl } from '../../../../api/apis.config'
import { getOlPermissions } from '../../../../lib/constants/ol-permissions'
import useAuth from '../../../../lib/hooks/use-auth'
import { useDownloadDocuments } from '../../../../lib/hooks/use-download-documents'
import useTranslate from '../../../../lib/hooks/use-translate'
import { CenterStatus } from '../../../../lib/types/enums/center/center-status'
import { OLTypeEnum } from '../../../../lib/types/enums/ol/ol-type.enum'
import { Center } from '../../../../lib/types/models/center/center'
import { AccordionState } from '../../../../lib/types/utils'
import CenterProgramsAndActivitiesPlanner from '../../common/center-programs-and-activities-calendar'
import { PlannerHandle } from '../../ol-common/ol-center-program/ol-center-weekly-planner/ol-center-weekly-planner'
import {
  CenterProgramAccordionKey,
  getCenterProgramAccordionState
} from '../../ol-common/ol-center-program/utils'
import { CenterSimpleProgramAndActivities } from '../../ols/centers/center-tab-views/center-program/center-simple-program-and-activities'
import OlPersonalSpaceCenterTeflList from './ol-personal-space-center-tefl-list'
import OlPersonalSpaceTeachingHallTable from './ol-personal-space-teaching-hall-table'

type Props = {
  id: number
  center: Center
}

const OlPersonalSpaceCenterProgramAndActivities = ({ id, center }: Props) => {
  const { user } = useAuth()
  const { downloadDocuments } = useDownloadDocuments()
  const plannerRef = useRef<PlannerHandle>(null)
  const { t } = useTranslate()

  const olId = user?.ol?.id
  const olType = center?.ol?.type

  const journeyPlace = center?.journeyPlace
  const timezone = journeyPlace?.timezone ?? journeyPlace?.country.timezone

  const permissions = getOlPermissions({ role: user.role, type: olType })

  const initialAccordionState = useMemo<AccordionState<CenterProgramAccordionKey>[]>(
    () =>
      getCenterProgramAccordionState(t, {
        'program-details': center.activitiesStatus === CenterStatus.OPEN && (
          <Button
            label={t('ol.center.program.activities.add.activity')}
            icon={PlusIcon}
            variant="primary"
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              plannerRef.current?.openCreateActivityModal()
            }}
          />
        )
      }),
    [t, center.activitiesStatus]
  )

  const [accordionState, setAccordionState] =
    useState<AccordionState<CenterProgramAccordionKey>[]>(initialAccordionState)

  const renderAccordionContent = (key: CenterProgramAccordionKey) => {
    switch (key) {
      case 'program-details':
        return (
          <CenterProgramsAndActivitiesPlanner
            plannerRef={plannerRef}
            allowCourseDeletion={false}
            olId={olId}
            center={center}
            currency={center.ol?.currency}
            mode="open"
          />
        )
      case 'tefl':
        return <OlPersonalSpaceCenterTeflList centerId={id} olId={olId} />
      case 'teaching-hall':
        return <OlPersonalSpaceTeachingHallTable centerId={id} olId={olId} />
      default:
        return null
    }
  }

  useEffect(() => {
    setAccordionState(initialAccordionState)
  }, [initialAccordionState])

  if (permissions['center-program-and-activities'])
    return (
      <CenterSimpleProgramAndActivities
        data={center}
        view={olType === OLTypeEnum.RESIDENCE ? 'addresses' : 'teaching-hall'}
      />
    )

  return (
    <div className="mt-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <Typography variant="sub-heading" color={tailwindColors.black.DEFAULT}>
          {t('ol.center.program.activities.title')}
        </Typography>
        <Badge
          variant="grey"
          iconPosition="start"
          icon={<ClockIcon color={tailwindColors.grey.DEFAULT} />}
          text={t('ol.center.program.activities.timezone', {
            timezone: timezone
          })}
        />
      </div>
      <div className="flex items-center gap-2 mb-3">
        <Button
          variant="ghost"
          label={t('ol.center.program.activities.print.program')}
          icon={PrintIcon}
          onClick={() =>
            downloadDocuments({
              endpoint: constructApiUrl(API.centers.printCenterActivityProgram.url, {
                id: center.id
              })
            })
          }
        />
        <Button
          variant="ghost"
          label={t('ol.center.program.activities.print.program.by.price')}
          icon={PrintIcon}
          onClick={() =>
            downloadDocuments({
              endpoint: constructApiUrl(API.centers.printCenterActivityProgramWithBudget.url, {
                id: center.id
              })
            })
          }
        />
        <Button
          variant="ghost"
          label={t('ol.center.program.activities.print.level.tests')}
          icon={PrintIcon}
          onClick={() =>
            downloadDocuments({
              endpoint: constructApiUrl(API.centers.printCenterlevelTests.url, { id: center.id })
            })
          }
        />
        <Button
          variant="ghost"
          label={t('ol.center.enrollment.table.actions.print.detailed.test.results')}
          icon={PrintIcon}
          onClick={() =>
            downloadDocuments({
              endpoint: constructApiUrl(API.centers.printCenterDetailedTestsResults.url, {
                id: center.id
              })
            })
          }
        />
      </div>

      {Object.values(accordionState).map(({ key, open, label, icon, actions }) => (
        <Accordion
          key={key}
          title={
            <div className="flex items-center gap-1.5">
              <Badge icon={icon} variant="warning" size="flexible" />
              <Typography variant="text" color={tailwindColors.black.DEFAULT}>
                {label}
              </Typography>
            </div>
          }
          subtitle={actions}
          open={open}
          className="bg-white-hover"
          variant="primary"
          onToggle={() =>
            setAccordionState((prev) =>
              prev.map((item) => (item.key === key ? { ...item, open: !item.open } : item))
            )
          }
        >
          {renderAccordionContent(key)}
        </Accordion>
      ))}
    </div>
  )
}

export default OlPersonalSpaceCenterProgramAndActivities
